import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOptionalDependency } from 'ditox-react';
import { useTranslation } from 'react-i18next';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import useGroupInfo from '@/hooks/groups/use-group-info';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import type { RespondedType, ResponseError } from '@/models/response';
import { useMessageStore } from '@/stores/message';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useQuitGroup(groupId: Nullable<number>, dismissible = false) {
  const { t } = useTranslation();
  const { isGroupOwner } = useGroupInfo(groupId);
  const webSocketManager = useOptionalDependency(WEB_SOCKET_MANAGER_TOKEN);
  const queryClient = useQueryClient();
  const { dispatch } = useCommDispatcher();

  return useMutation<RespondedType, ResponseError>({
    mutationKey: ['/QuitGroup', groupId],
    mutationFn: groupId
      ? () => fetcher(isGroupOwner ? '/DeleteGroup' : '/QuitGroup', { GroupID: groupId }, { validResponseCodes: [0] })
      : undefined,
    onSuccess: async () => {
      if (!groupId) return;

      if (dismissible) dispatch({ event: 'dismiss' });
      dispatch({
        event: 'leave-session',
        data: { type: 'group', id: groupId },
      });

      webSocketManager?.dispatch({
        action: 'unsubscribe',
        params: { groupId },
      });

      const { deleteBySessionId, dehydrate } = useMessageStore.getState();
      await Promise.allSettled([
        queryClient.invalidateQueries(groupsQueryOptions),
        queryClient.invalidateQueries(allChatSessionQueryOptions),
        deleteBySessionId('group', groupId),
      ]);
      dehydrate();
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: error ? error.message : isGroupOwner ? data?.Msg : t('conversation.group.has-left'),
      }),
  });
}
